import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core'
import { Store } from '@ngrx/store'
import { selectRunningPayroll } from '../../../store/payroll/payroll.reducers'
import { CommonModule, NgFor } from '@angular/common'
import { combineLatest, filter, map, Observable, Subject, take, takeUntil, tap } from 'rxjs'
import {
  IncomingPayroll,
  IncomingRunningPayrollBatch,
  PayrollStatus
} from '../../../store/payroll/payroll.state'
import { selectBusinesses } from '../../../store/business/business.reducer'
import { loadBusinesses } from '../../../store/business/business.actions'
import { eps } from '../../../services/url.service'
import { RouterModule } from '@angular/router'
import { environment } from '../../../../environments/environment'
import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { KeySettingsComponent } from '../key-settings/key-settings.component'
import { PayslipComponent } from '../payslip/payslip.component'
import { runningPayrollActions } from '../../../store/payroll/payroll.actions'

@Component({
  selector: 'app-review',
  imports: [NgFor, RouterModule, CommonModule, CapitalizePipe],
  templateUrl: './review.component.html',
  styleUrl: './review.component.scss'
})
export class ReviewComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private modalService = inject(NgbModal)
  @Output() nextScreen = new EventEmitter<boolean>()
  private destroy$ = new Subject<void>()
  KeySettingsComponent = KeySettingsComponent
  PayslipComponent = PayslipComponent
  imageBaseUrl = environment.apiBaseUrl + '/uploads/'
  eps = eps
  PayrollStatus = PayrollStatus
  runningPayroll$ = this.store.select(selectRunningPayroll)
  businessEntity$!: Observable<string | undefined>
  currentIncomingPayrolls: IncomingPayroll[] = []
  payPeriodStartDate = ''
  //currentPage = 1
  //pageLimit = 10
  //totalCount = 0
  //pageLastRecord = 0

  changeScreen(value: boolean) {
    this.nextScreen.emit(value)
  }

  ngOnInit(): void {
    this.store.dispatch(loadBusinesses({}))
    this.initializeBusinessEntityObservable()

    this.runningPayroll$
      .pipe(
        filter(
          (runningPayroll): runningPayroll is IncomingRunningPayrollBatch =>
            // Type guard
            !!runningPayroll &&
            !!runningPayroll.payPeriodStart &&
            !!runningPayroll.payPeriodEnd &&
            !!runningPayroll.belongsTo
        ),
        take(1),
        tap(runningPayroll => {
          this.store.dispatch(
            runningPayrollActions.loadRunningPayroll({
              payPeriodStart: runningPayroll.payPeriodStart,
              payPeriodEnd: runningPayroll.payPeriodEnd,
              belongsTo: runningPayroll.belongsTo
            })
          )
        })
      )
      .subscribe()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private initializeBusinessEntityObservable(): void {
    this.businessEntity$ = combineLatest([
      this.runningPayroll$.pipe(filter((rp): rp is IncomingRunningPayrollBatch => !!rp)),
      this.store
        .select(selectBusinesses)
        .pipe(filter(businesses => !!businesses && businesses.length > 0))
    ]).pipe(
      map(([runningPayroll, businesses]) => {
        this.currentIncomingPayrolls = runningPayroll.payrolls
        this.payPeriodStartDate = runningPayroll.payPeriodStart
        return businesses.find(b => b.id === runningPayroll.belongsTo)?.businessName
      }),
      takeUntil(this.destroy$)
    )
  }

  // Helper to get employee name initials
  getInitials(firstName = '', lastName = ''): string {
    const firstInitial = firstName.charAt(0)
    const lastInitial = lastName.charAt(0)
    return (firstInitial + lastInitial).toUpperCase()
  }

  //onPageChange(newPage: number): void {}

  openModal(modal: unknown, index: number) {
    const modalRef = this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'lg'
    })
    if (modalRef.componentInstance) {
      modalRef.componentInstance.payrollData = this.currentIncomingPayrolls[index]
      modalRef.componentInstance.payPeriodStartDate = this.payPeriodStartDate
    }
  }

  confirmPayroll() {
    const data: { id: string; status: string; payrollIds: string[] } = {
      id: this.currentIncomingPayrolls[0].batchId as string,
      status: PayrollStatus.PROCESSED,
      payrollIds: []
    }

    this.currentIncomingPayrolls.forEach(payroll => {
      data.payrollIds.push(payroll.id as string)
    })

    this.store.dispatch(runningPayrollActions.updateRunningPayrollStatus({ ...data }))
    this.changeScreen(true)
  }

  getDeductionAmount(payroll: any, name: string): number {
    if (!payroll?.calculationBreakdown?.deductions) {
      return 0
    }

    const deduction = payroll.calculationBreakdown.deductions.find((d: any) => d.name === name)

    return deduction ? deduction.amount : 0
  }
}
